import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ASYNC_STATUS } from "constants/StatusConstant";
import apiHandle from "services/apiHandle";

const initialState = {
  drivers: [],
  selectedDriver: null,

  add_driver_status: ASYNC_STATUS.IDLE,
  add_driver_error: null,

  update_driver_status: ASYNC_STATUS.IDLE,
  update_driver_error: null,

  get_drivers_status: ASYNC_STATUS.IDLE,
  get_drivers_error: null,

  get_driver_by_id_status: ASYNC_STATUS.IDLE,
  get_driver_by_id_error: null,

  delete_driver_status: ASYNC_STATUS.IDLE,
  delete_driver_error: null,
};

export const addDriverAsync = createAsyncThunk(
  "/add-driver",
  async (body, { rejectWithValue }) => {
    try {
      const jsonBody = {
        firstName: body.firstName,
        lastName: body.lastName,
        email: body.email,
        phoneNumber: body.phoneNumber,
        licenseNumber: body.licenseNumber,
        licenseExpiryDate: body.licenseExpiryDate || null,
        canEditProfile: body.canEditProfile,
        canBookService: body.canBookService,
        vehicleId: body.vehicleId || "",
        avatar: body.avatar || "",
      };

      const res = await apiHandle("/drivers/add", "POST", jsonBody);
      return res;
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to add driver");
    }
  },
);

export const getUserDriversAsync = createAsyncThunk(
  "/get-user-drivers",
  async (_, { rejectWithValue }) => {
    try {
      const res = await apiHandle("/drivers/getall", "GET");
      return res;
    } catch (err) {
      return rejectWithValue(err?.message || "Session expired");
    }
  },
);

export const updateDriverAsync = createAsyncThunk(
  "/update-driver",
  async ({ id, body }, { rejectWithValue }) => {
    try {
      // Always send as JSON - avatar should be URL string
      const jsonBody = {
        firstName: body.firstName,
        lastName: body.lastName,
        email: body.email,
        phoneNumber: body.phoneNumber,
        licenseNumber: body.licenseNumber,
        licenseExpiryDate: body.licenseExpiryDate || null,
        canEditProfile: body.canEditProfile,
        canBookService: body.canBookService,
        vehicleId: body.vehicleId || "",
        avatar: body.avatar || "",
      };

      const res = await apiHandle(`/drivers/update/${id}`, "PATCH", jsonBody);
      return res;
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to update driver");
    }
  },
);

export const getAllDriversAsync = createAsyncThunk(
  "/get-all-drivers",
  async (_, { rejectWithValue }) => {
    try {
      const res = await apiHandle("/drivers/getall", "GET");
      return res;
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch drivers");
    }
  },
);

export const getDriverByIdAsync = createAsyncThunk(
  "/get-driver-by-id",
  async (id, { rejectWithValue }) => {
    try {
      const res = await apiHandle(`/drivers/getbyid/${id}`, "GET");
      return res;
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch driver");
    }
  },
);

export const deleteDriverAsync = createAsyncThunk(
  "/delete-driver",
  async (id, { rejectWithValue }) => {
    try {
      const res = await apiHandle(`/drivers/delete/${id}`, "DELETE");
      return res;
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to delete driver");
    }
  },
);

const driverSlice = createSlice({
  name: "driver",
  initialState,
  reducers: {
    resetDriverState: (state, action) => {
      const stateKey = action.payload;
      state[stateKey] = ASYNC_STATUS.IDLE;
    },
    clearSelectedDriver: (state) => {
      state.selectedDriver = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // add driver
      .addCase(addDriverAsync.pending, (state) => {
        state.add_driver_status = ASYNC_STATUS.LOADING;
        state.add_driver_error = null;
      })
      .addCase(addDriverAsync.fulfilled, (state, action) => {
        state.add_driver_status = ASYNC_STATUS.SUCCEEDED;
        const { driver } = action.payload;
        state.drivers = [driver, ...state.drivers];
        state.add_driver_error = null;
      })
      .addCase(addDriverAsync.rejected, (state, action) => {
        state.add_driver_status = ASYNC_STATUS.ERROR;
        state.add_driver_error =
          action.payload?.message || action.error.message;
      })

      // update driver
      .addCase(updateDriverAsync.pending, (state) => {
        state.update_driver_status = ASYNC_STATUS.LOADING;
        state.update_driver_error = null;
      })
      .addCase(updateDriverAsync.fulfilled, (state, action) => {
        state.update_driver_status = ASYNC_STATUS.SUCCEEDED;
        // API might return driver in payload.data or payload.driver or directly in payload
        const updatedDriver =
          action.payload?.data || action.payload?.driver || action.payload;

        if (updatedDriver && updatedDriver._id) {
          const index = state.drivers.findIndex(
            (d) => d._id === updatedDriver._id,
          );
          if (index !== -1) {
            state.drivers[index] = updatedDriver;
          }
        }
        state.update_driver_error = null;
      })
      .addCase(updateDriverAsync.rejected, (state, action) => {
        state.update_driver_status = ASYNC_STATUS.ERROR;
        state.update_driver_error =
          action.payload?.message || action.error.message;
      })

      // get user drivers
      .addCase(getUserDriversAsync.pending, (state) => {
        state.get_drivers_status = ASYNC_STATUS.LOADING;
        state.get_drivers_error = null;
      })
      .addCase(getUserDriversAsync.fulfilled, (state, action) => {
        state.get_drivers_status = ASYNC_STATUS.SUCCEEDED;
        const { data } = action.payload;
        state.drivers = data || [];
        state.get_drivers_error = null;
      })
      .addCase(getUserDriversAsync.rejected, (state, action) => {
        state.get_drivers_status = ASYNC_STATUS.ERROR;
        state.get_drivers_error =
          action.payload?.message || action.error.message;
      })

      // get all drivers
      .addCase(getAllDriversAsync.pending, (state) => {
        state.get_drivers_status = ASYNC_STATUS.LOADING;
        state.get_drivers_error = null;
      })
      .addCase(getAllDriversAsync.fulfilled, (state, action) => {
        state.get_drivers_status = ASYNC_STATUS.SUCCEEDED;
        // apiHandle returns the API body: { success, data: [...], count, pagination }
        const payload = action.payload;
        state.drivers = payload?.data || payload?.drivers || [];
        state.get_drivers_error = null;
      })
      .addCase(getAllDriversAsync.rejected, (state, action) => {
        state.get_drivers_status = ASYNC_STATUS.ERROR;
        state.get_drivers_error =
          action.payload?.message || action.error.message;
      })

      // get driver by id
      .addCase(getDriverByIdAsync.pending, (state) => {
        state.get_driver_by_id_status = ASYNC_STATUS.LOADING;
        state.get_driver_by_id_error = null;
      })
      .addCase(getDriverByIdAsync.fulfilled, (state, action) => {
        state.get_driver_by_id_status = ASYNC_STATUS.SUCCEEDED;
        const { data } = action.payload;
        state.selectedDriver = data;
        state.get_driver_by_id_error = null;
      })
      .addCase(getDriverByIdAsync.rejected, (state, action) => {
        state.get_driver_by_id_status = ASYNC_STATUS.ERROR;
        state.get_driver_by_id_error =
          action.payload?.message || action.error.message;
      })

      // delete driver
      .addCase(deleteDriverAsync.pending, (state) => {
        state.delete_driver_status = ASYNC_STATUS.LOADING;
        state.delete_driver_error = null;
      })
      .addCase(deleteDriverAsync.fulfilled, (state, action) => {
        state.delete_driver_status = ASYNC_STATUS.SUCCEEDED;
        const { deletedDriverId } = action.payload;
        state.drivers = state.drivers.filter((d) => d._id !== deletedDriverId);
      })
      .addCase(deleteDriverAsync.rejected, (state, action) => {
        state.delete_driver_status = ASYNC_STATUS.ERROR;
        state.delete_driver_error =
          action.payload?.message || action.error.message;
      });
  },
});

export const { resetDriverState, clearSelectedDriver } = driverSlice.actions;
export default driverSlice.reducer;
